import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import pluralize from 'pluralize';
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {EyeIcon, NewspaperIcon} from "@heroicons/react/outline";
import {NewspaperIcon as NewspaperIconSolid, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {Article} from "types/Article";
import {useNavigate} from "react-router-dom";
import DeleteArticle from '../Delete';
import DotBadge from "components/DotBadge";
import ArticleStatus from "types/ArticleStatus";
import BooleanIndicator from "components/BooleanIndicator";

// Map message status to badge color
const statusColors: Record<ArticleStatus, number> = {
    [ArticleStatus.UNPUBLISHED]: DotBadge.colors.GRAY,
    [ArticleStatus.SCHEDULED]: DotBadge.colors.YELLOW,
    [ArticleStatus.PUBLISHED]: DotBadge.colors.GREEN,
}

function ArticleList() {
    const navigate = useNavigate()

    const [deletingArticle, setDeletingArticle] = useState<Article | null>(null);
    const [page, setPage] = useState<number>(1);

    const {data: articlePage, isLoading} = useQuery(
        ['articles.all', {page}],
        () => ngage.articles.filtered({page}),
        {keepPreviousData: true}
    )

    // Prefetch next and previous pages
    prefetch('articles.all', {page}, ngage.articles.filtered)

    // Prepare breadcrumbs, metadata and actions
    const breadcrumbs = {'App Content': '/', 'Featured': '/', 'News': '/articles'}
    const meta = [{icon: NewspaperIconSolid, value: pluralize('article', articlePage?.meta.total || 0, true)}]
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/articles/new')}>New</Button>

    return (
        <Page title="News" breadcrumbs={breadcrumbs} meta={meta} actions={actions}>
            <Table paginationData={articlePage} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Title</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader>Author</TableHeader>
                        <TableHeader alignment={TableHeader.alignments.CENTER}>
                            Featured
                        </TableHeader>
                        <TableHeader>Status</TableHeader>
                        <TableHeader>Publish Date</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={isLoading}>

                    {articlePage && articlePage.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={7}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <NewspaperIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No articles</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : articlePage?.data.map((article, index) => (
                        <TableRow key={article.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position='left'>
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/articles/${article.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/articles/${article.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingArticle(article)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <NewspaperIcon className="h-5 w-5 mr-2 text-gray-500" /> {truncate(article.title, 35)}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{article.is_external? 'External' : 'Internal'}</TableData>
                            <TableData>{article.author?.name || 'Unknown'}</TableData>
                            <TableData alignment={TableData.alignments.CENTER}>
                                <BooleanIndicator value={article.is_featured} />
                            </TableData>
                            <TableData transform={TableData.transforms.CAPITALIZE}>
                                <DotBadge color={statusColors[article.status]} text={article.status} />
                            </TableData>
                            <TableData>{formattedDateTime(article.published_at) ?? 'Never'}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/articles/${article.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/articles/${article.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingArticle(article)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteArticle
                                article={deletingArticle}
                                show={article === deletingArticle}
                                onCancel={() => setDeletingArticle(null)}
                                onDelete={() => navigate('/articles')}
                                onDeleteFail={() => setDeletingArticle(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default ArticleList;